import { sortIvrMenuDtmfKeys } from "./ivrSchemeConstants";
import { normalizeIvrSchedulerScheduleKind } from "./ivrElementDraftData";
import {
    type IvrApiElement,
    type IvrApiRoute,
    type IvrApiSchedule,
    type IvrSchedulerSlotRow,
    elementToBlockConfig,
    ivrRawScheduleActiveToId,
    ivrRawScheduleElementId,
    ivrRawScheduleIsActive,
    ivrSchedulerSlotNodeIdSuffix,
    ivrSchedulerSlotRowIndexFromSlotSuffix,
    resolveEffectiveIvrEntryElementId,
} from "./ivrSchemeHydration";

const MAX_PLAYBACK_STEPS = 128;
const START_CONFIG_PREFIX = "start-config-";
const BASE_NODE_ID = "ivr-base";
const CHAIN_PREFIX = "start-chain-";

export type IvrPlaybackGraphNode = {
    id: string;
    position?: { x: number; y: number };
    data?: {
        label?: string;
        blockConfig?: Record<string, unknown>;
        configParentId?: string;
        isGenerated?: boolean;
    };
};

export type IvrPlaybackGraphEdge = {
    source: string;
    target: string;
};

export type IvrPlaybackPlan = {
    key: string;
    label: string;
    nodeIds: string[];
};

export const IVR_SCHEME_PLAYBACK_STEP_MS = 650;
export const IVR_SCHEME_PLAYBACK_PLAN_GAP_MS = 500;

function normalizeRouteType(routeType: string | null | undefined): string {
    return (routeType ?? "").trim().toUpperCase().replace(/-/g, "_");
}

function routesFromElementSorted(
    elementId: number,
    routes: IvrApiRoute[]
): IvrApiRoute[] {
    return routes
        .filter(
            (r) => r.fromElementId === elementId && r.isActive !== false
        )
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
}

function resolveElementId(raw: unknown): number | null {
    const num = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(num) ? num : null;
}

function buildOutgoingIndex(
    edges: IvrPlaybackGraphEdge[]
): Map<string, string[]> {
    const out = new Map<string, string[]>();
    for (const e of edges) {
        const list = out.get(e.source) ?? [];
        list.push(e.target);
        out.set(e.source, list);
    }
    out.forEach((list, key) => {
        out.set(key, [...list]);
    });
    return out;
}

function buildElementIdToConfigNodeId(
    nodes: IvrPlaybackGraphNode[]
): Map<number, string> {
    const map = new Map<number, string>();
    for (const n of nodes) {
        if (!n.id.startsWith(START_CONFIG_PREFIX)) continue;
        const elId = resolveElementId(n.data?.blockConfig?.elementId);
        if (elId != null) map.set(elId, n.id);
    }
    return map;
}

function sortNodesByLayout(
    ids: string[],
    nodesById: Map<string, IvrPlaybackGraphNode>
): string[] {
    return [...ids].sort((a, b) => {
        const na = nodesById.get(a);
        const nb = nodesById.get(b);
        const dy = (na?.position?.y ?? 0) - (nb?.position?.y ?? 0);
        if (dy !== 0) return dy;
        return (na?.position?.x ?? 0) - (nb?.position?.x ?? 0);
    });
}

function isMenuDtmfHubNode(nodeId: string): boolean {
    return nodeId.endsWith("-dtmf");
}

function menuDtmfHubId(configNodeId: string): string {
    return `${CHAIN_PREFIX}${configNodeId}-dtmf`;
}

function menuDtmfKeyNodeId(configNodeId: string, key: string): string {
    return `${CHAIN_PREFIX}${configNodeId}-dtmf-${key}`;
}

function sortedMenuKeysForConfig(
    configNodeId: string,
    nodesById: Map<string, IvrPlaybackGraphNode>,
    routes: IvrApiRoute[],
    elementId: number | null
): string[] {
    const cfg = nodesById.get(configNodeId);
    const fromConfig = sortIvrMenuDtmfKeys(
        cfg?.data?.blockConfig?.menuDtmfKeys as string[] | undefined
    );
    if (fromConfig.length > 0) return fromConfig;
    if (elementId != null) {
        return sortIvrMenuDtmfKeys(
            deriveMenuDtmfKeysFromRoutes(elementId, routes)
        );
    }
    return [];
}

function deriveMenuDtmfKeysFromRoutes(
    elementId: number,
    routes: IvrApiRoute[]
): string[] {
    const keys = new Set<string>();
    for (const r of routesFromElementSorted(elementId, routes)) {
        const nt = normalizeRouteType(r.routeType);
        const raw = (r.routeValue ?? "").trim();
        if (!raw) continue;
        if (nt === "DTMF" || nt === "DTMF_EXCEEDED") {
            keys.add(raw);
        }
    }
    return sortIvrMenuDtmfKeys(Array.from(keys));
}

function pickRouteForElement(
    fromElementId: number,
    routes: IvrApiRoute[],
    dtmfKeyByElement: ReadonlyMap<number, string>,
    routeOverrideByElement?: ReadonlyMap<number, IvrApiRoute>
): IvrApiRoute | null {
    const forced = routeOverrideByElement?.get(fromElementId);
    if (forced) return forced;

    const outs = routesFromElementSorted(fromElementId, routes);
    if (!outs.length) return null;

    const key = dtmfKeyByElement.get(fromElementId);
    if (key) {
        for (const r of outs) {
            const nt = normalizeRouteType(r.routeType);
            const rv = (r.routeValue ?? "").trim();
            if (
                (nt === "DTMF" || nt === "DTMF_EXCEEDED") &&
                rv === key
            ) {
                return r;
            }
        }
        return null;
    }
    return outs[0];
}

function menuDtmfKeysWithRoutes(
    elementId: number,
    keys: string[],
    routes: IvrApiRoute[]
): string[] {
    return keys.filter((key) =>
        routes.some(
            (r) =>
                r.fromElementId === elementId &&
                r.isActive !== false &&
                normalizeRouteType(r.routeType) === "DTMF" &&
                (r.routeValue ?? "").trim() === key
        )
    );
}

function resolveRouteTargetElementId(
    fromElementId: number,
    routes: IvrApiRoute[],
    dtmfKey?: string
): number | null {
    const pick = new Map<number, string>();
    if (dtmfKey) pick.set(fromElementId, dtmfKey);
    return pickRouteForElement(fromElementId, routes, pick)?.toElementId ?? null;
}

function resolveNextElementIdWithPick(
    currentId: number,
    elementsById: Map<number, IvrApiElement>,
    routes: IvrApiRoute[],
    schedules: IvrApiSchedule[] | undefined,
    dtmfKeyByElement: ReadonlyMap<number, string>,
    routeOverrideByElement?: ReadonlyMap<number, IvrApiRoute>
): number | null {
    const el = elementsById.get(currentId);
    if (!el) return null;

    const config = elementToBlockConfig(el, routes, schedules);
    const configType = String(config.startConfiguration ?? "Play");

    if (configType === "Scheduler") {
        return schedulerFirstChildElementId(
            currentId,
            config,
            schedules,
            elementsById
        );
    }

    const picked = pickRouteForElement(
        currentId,
        routes,
        dtmfKeyByElement,
        routeOverrideByElement
    );
    if (picked) return picked.toElementId;

    const schedSorted = [...(schedules ?? [])]
        .filter((s) => ivrRawScheduleIsActive(s))
        .filter((s) => ivrRawScheduleElementId(s) === currentId)
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    for (const s of schedSorted) {
        const tid = ivrRawScheduleActiveToId(s);
        if (tid !== 0 && elementsById.has(tid)) return tid;
    }

    return null;
}

function buildElementIdPath(
    entryElementId: number,
    elementsById: Map<number, IvrApiElement>,
    routes: IvrApiRoute[],
    schedules: IvrApiSchedule[] | undefined,
    dtmfKeyByElement: ReadonlyMap<number, string>,
    routeOverrideByElement?: ReadonlyMap<number, IvrApiRoute>
): number[] {
    const path: number[] = [];
    const visited = new Set<number>();
    let current: number | null = entryElementId;

    while (
        current != null &&
        current > 0 &&
        !visited.has(current) &&
        path.length < MAX_PLAYBACK_STEPS
    ) {
        visited.add(current);
        path.push(current);

        const el = elementsById.get(current);
        if (!el) break;
        if (String(el.elementType ?? "").toUpperCase() === "END") break;

        current = resolveNextElementIdWithPick(
            current,
            elementsById,
            routes,
            schedules,
            dtmfKeyByElement,
            routeOverrideByElement
        );
    }

    return path;
}

function uniqueOutgoingRoutes(
    elementId: number,
    routes: IvrApiRoute[]
): IvrApiRoute[] {
    const outs = routesFromElementSorted(elementId, routes);
    const seen = new Set<string>();
    const unique: IvrApiRoute[] = [];
    for (const r of outs) {
        const sig = `${r.toElementId}|${normalizeRouteType(r.routeType)}|${String(r.routeValue ?? "").trim()}`;
        if (seen.has(sig)) continue;
        seen.add(sig);
        unique.push(r);
    }
    return unique;
}

function routeBranchLabel(route: IvrApiRoute): string {
    const nt = normalizeRouteType(route.routeType);
    const rv = (route.routeValue ?? "").trim();
    if (nt === "DTMF" || nt === "DTMF_EXCEEDED") {
        return rv ? `Кнопка «${rv}»` : "Кнопка";
    }
    if (rv) return `${nt || "Маршрут"}: ${rv}`;
    return `Маршрут → ${route.toElementId}`;
}

function bfsGraphPath(
    fromId: string,
    toId: string,
    outIndex: Map<string, string[]>,
    requiredVia?: string
): string[] | null {
    if (fromId === toId) return [fromId];

    const queue: string[] = [fromId];
    const parent = new Map<string, string | null>();
    parent.set(fromId, null);

    while (queue.length) {
        const cur = queue.shift()!;
        if (cur === toId) break;
        for (const next of outIndex.get(cur) ?? []) {
            if (parent.has(next)) continue;
            parent.set(next, cur);
            queue.push(next);
        }
    }

    if (!parent.has(toId)) return null;

    const path: string[] = [];
    let node: string | null = toId;
    while (node) {
        path.unshift(node);
        node = parent.get(node) ?? null;
    }

    if (requiredVia && !path.includes(requiredVia)) {
        const viaPath = bfsGraphPath(fromId, requiredVia, outIndex);
        const tailPath = bfsGraphPath(requiredVia, toId, outIndex);
        if (!viaPath?.length || !tailPath?.length) return path;
        return mergeNodePaths(viaPath, tailPath.slice(1));
    }

    return path;
}

function elementPathToNodeIds(
    elementIds: number[],
    startNodeId: string | null,
    elementToConfig: Map<number, string>,
    outIndex: Map<string, string[]>,
    nodesById: Map<string, IvrPlaybackGraphNode>,
    dtmfKeyByElement: ReadonlyMap<number, string>,
    ctx: WalkContext
): string[] {
    const out: string[] = [];
    if (startNodeId && nodesById.has(startNodeId)) {
        out.push(startNodeId);
    }

    let prevElementId: number | null = elementIds[0] ?? null;
    let prevConfigId: string | null =
        startNodeId?.startsWith(START_CONFIG_PREFIX) ? startNodeId : null;

    for (const elId of elementIds) {
        const cfg = elementToConfig.get(elId);
        if (!cfg || !nodesById.has(cfg)) continue;

        const anchor = out[out.length - 1];
        if (anchor && anchor !== cfg) {
            const viaKey =
                prevElementId != null
                    ? dtmfKeyByElement.get(prevElementId)
                    : undefined;
            const viaNode =
                prevConfigId && viaKey
                    ? menuDtmfKeyNodeId(prevConfigId, viaKey)
                    : undefined;

            let segment: string[] | null = null;
            if (nodesById.has(anchor)) {
                segment = bfsGraphPath(anchor, cfg, outIndex, viaNode);
            }
            if (!segment?.length && viaNode && nodesById.has(viaNode)) {
                const viaWalk = walkGraphPath(viaNode, {
                    ...ctx,
                    menuDtmfKey: viaKey,
                });
                const cfgIdx = viaWalk.indexOf(cfg);
                segment =
                    cfgIdx >= 0
                        ? mergeNodePaths(
                              bfsGraphPath(anchor, viaNode, outIndex) ?? [
                                  anchor,
                                  viaNode,
                              ],
                              viaWalk.slice(0, cfgIdx + 1)
                          )
                        : viaWalk;
            }
            if (segment?.length) {
                for (const id of segment) {
                    if (out[out.length - 1] === id) continue;
                    out.push(id);
                }
            } else if (!out.includes(cfg)) {
                out.push(cfg);
            }
        } else if (!out.includes(cfg)) {
            out.push(cfg);
        }

        prevElementId = elId;
        prevConfigId = cfg;
    }

    return out;
}

function schedulerFirstChildElementId(
    elementId: number,
    config: Record<string, unknown>,
    schedules: IvrApiSchedule[] | undefined,
    elementsById: Map<number, IvrApiElement>
): number | null {
    const kind = normalizeIvrSchedulerScheduleKind(
        config.schedulerScheduleKind
    );

    const tryId = (tid: number): number | null => {
        if (tid === 0 || !elementsById.has(tid)) return null;
        return tid;
    };

    if (kind === "once") {
        const rawAid = config.schedulerActiveToId;
        const aid = typeof rawAid === "number" ? rawAid : Number(rawAid);
        if (Number.isFinite(aid) && aid > 0) {
            const hit = tryId(aid);
            if (hit != null) return hit;
        }
        for (const s of schedules ?? []) {
            if (!ivrRawScheduleIsActive(s)) continue;
            if (ivrRawScheduleElementId(s) !== elementId) continue;
            const hit = tryId(ivrRawScheduleActiveToId(s));
            if (hit != null) return hit;
        }
        return null;
    }

    const sr =
        (config.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ??
        [];
    const sorted = [...sr].sort(
        (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
    );
    for (const row of sorted) {
        const hit = tryId(row.activeToId);
        if (hit != null) return hit;
    }
    return null;
}

type SchedulerPlaybackBranch =
    | { type: "day"; dayOfWeek: number }
    | { type: "slot"; slotIndex: number }
    | { type: "chain"; nodeId: string }
    | { type: "others" };

function schedulerDailyPrimaryNodeId(configNodeId: string): string {
    return `${CHAIN_PREFIX}${configNodeId}-daily-primary`;
}

function schedulerDailyRestNodeId(configNodeId: string): string {
    return `${CHAIN_PREFIX}${configNodeId}-daily-rest`;
}

function sortedSchedulerSlotRowsFromConfigNode(
    configNodeId: string,
    ctx: WalkContext
): IvrSchedulerSlotRow[] {
    const node = ctx.nodesById.get(configNodeId);
    const rows =
        (node?.data?.blockConfig?.schedulerSlotRows as
            | IvrSchedulerSlotRow[]
            | undefined) ?? [];
    return [...rows].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
}

function schedulerSlotNodeIdForIndex(
    configNodeId: string,
    sortedRows: IvrSchedulerSlotRow[],
    slotIndex: number
): string {
    return `${CHAIN_PREFIX}${configNodeId}-sched-slot-${ivrSchedulerSlotNodeIdSuffix(sortedRows, slotIndex)}`;
}

function orderSchedulerSlotNodeIds(
    configNodeId: string,
    slotNodeIds: string[],
    ctx: WalkContext
): string[] {
    const sortedRows = sortedSchedulerSlotRowsFromConfigNode(
        configNodeId,
        ctx
    );
    const prefix = `${CHAIN_PREFIX}${configNodeId}-sched-slot-`;
    return [...slotNodeIds].sort((a, b) => {
        const idxA = ivrSchedulerSlotRowIndexFromSlotSuffix(
            a.startsWith(prefix) ? a.slice(prefix.length) : a,
            sortedRows
        );
        const idxB = ivrSchedulerSlotRowIndexFromSlotSuffix(
            b.startsWith(prefix) ? b.slice(prefix.length) : b,
            sortedRows
        );
        return (idxA < 0 ? 999 : idxA) - (idxB < 0 ? 999 : idxB);
    });
}

function schedulerSlotLabel(
    row: IvrSchedulerSlotRow,
    nodeLabel?: string
): string {
    const fromNode = (nodeLabel ?? "").replace(/\n/g, " ").trim();
    if (fromNode) return fromNode;
    const start = String(row.start ?? "").trim();
    const end = String(row.end ?? "").trim();
    if (start || end) return `${start} - ${end}`.trim();
    return `Слот ${(row.priority ?? 0) + 1}`;
}

type WalkContext = {
    nodesById: Map<string, IvrPlaybackGraphNode>;
    outIndex: Map<string, string[]>;
    routes: IvrApiRoute[];
    schedules?: IvrApiSchedule[];
    elementsById: Map<number, IvrApiElement>;
    elementToConfig: Map<number, string>;
    menuDtmfKey?: string;
    lockedDtmfKeyByElement?: ReadonlyMap<number, string>;
    lockedSchedulerBranchByElement?: ReadonlyMap<
        number,
        SchedulerPlaybackBranch
    >;
    retriesOutcome?: "true" | "false";
    stopAtDtmfHub?: boolean;
};

function isSchedulerConfigNode(
    nodeId: string,
    ctx: WalkContext
): boolean {
    const node = ctx.nodesById.get(nodeId);
    if (!node?.id.startsWith(START_CONFIG_PREFIX)) return false;
    return (
        String(node.data?.blockConfig?.startConfiguration ?? "") ===
        "Scheduler"
    );
}

function schedulerDayNodeId(
    configNodeId: string,
    dayOfWeek: number
): string {
    return `${CHAIN_PREFIX}${configNodeId}-day-${dayOfWeek}`;
}

function schedulerOthersNodeId(configNodeId: string): string {
    return `${CHAIN_PREFIX}${configNodeId}-sched-others`;
}

function menuElementIdFromChainNode(
    nodeId: string,
    nodesById: Map<string, IvrPlaybackGraphNode>
): number | null {
    const node = nodesById.get(nodeId);
    const parentId = node?.data?.configParentId;
    if (!parentId) return null;
    const parent = nodesById.get(parentId);
    if (!parent?.id.startsWith(START_CONFIG_PREFIX)) return null;
    return resolveElementId(parent.data?.blockConfig?.elementId);
}

function findMenuRouteForKey(
    elementId: number,
    routes: IvrApiRoute[],
    key: string,
    exceeded: boolean
): IvrApiRoute | null {
    const want = exceeded ? "DTMF_EXCEEDED" : "DTMF";
    for (const r of routesFromElementSorted(elementId, routes)) {
        const nt = normalizeRouteType(r.routeType);
        const rv = (r.routeValue ?? "").trim();
        if (nt === want && rv === key) {
            return r;
        }
    }
    return null;
}

type PlaybackBranchLocks = {
    dtmfKeyByElement: Map<number, string>;
    routeOverrideByElement: Map<number, IvrApiRoute>;
    schedulerBranchByElement: Map<number, SchedulerPlaybackBranch>;
    retriesOutcome: "true" | "false";
};

function resolveSchedulerDayFromChainNode(
    fromNodeId: string,
    nodesById: Map<string, IvrPlaybackGraphNode>,
    edges: IvrPlaybackGraphEdge[]
): { elementId: number; dayOfWeek: number } | null {
    const direct = fromNodeId.match(/-day-(\d+)$/);
    if (direct) {
        const elId = menuElementIdFromChainNode(fromNodeId, nodesById);
        if (elId != null) {
            return {
                elementId: elId,
                dayOfWeek: parseInt(direct[1]!, 10),
            };
        }
    }
    if (fromNodeId.includes("-sched-slot-")) {
        for (const e of edges) {
            if (e.target !== fromNodeId) continue;
            const m = e.source.match(/-day-(\d+)$/);
            if (!m) continue;
            const elId = menuElementIdFromChainNode(e.source, nodesById);
            if (elId != null) {
                return {
                    elementId: elId,
                    dayOfWeek: parseInt(m[1]!, 10),
                };
            }
        }
    }
    return null;
}

function resolveSchedulerBranchFromChainNode(
    fromNodeId: string,
    nodesById: Map<string, IvrPlaybackGraphNode>,
    edges: IvrPlaybackGraphEdge[]
): { elementId: number; branch: SchedulerPlaybackBranch } | null {
    if (
        fromNodeId.endsWith("-sched-others") ||
        fromNodeId.endsWith("-daily-primary") ||
        fromNodeId.endsWith("-daily-rest")
    ) {
        const elId = menuElementIdFromChainNode(fromNodeId, nodesById);
        if (elId != null) {
            if (fromNodeId.endsWith("-sched-others")) {
                return { elementId: elId, branch: { type: "others" } };
            }
            return {
                elementId: elId,
                branch: { type: "chain", nodeId: fromNodeId },
            };
        }
    }
    if (fromNodeId.includes("-sched-slot-")) {
        const elId = menuElementIdFromChainNode(fromNodeId, nodesById);
        const parentId = nodesById.get(fromNodeId)?.data?.configParentId;
        if (elId != null && parentId) {
            const configNodeId = parentId.startsWith(START_CONFIG_PREFIX)
                ? parentId
                : nodesById.get(parentId)?.data?.configParentId;
            if (configNodeId?.startsWith(START_CONFIG_PREFIX)) {
                const rows = sortedSchedulerSlotRowsFromConfigNode(
                    configNodeId,
                    {
                        nodesById,
                        outIndex: new Map(),
                        routes: [],
                        elementsById: new Map(),
                        elementToConfig: new Map(),
                    }
                );
                const m = fromNodeId.match(/-sched-slot-(.+)$/);
                if (m) {
                    const slotIndex =
                        ivrSchedulerSlotRowIndexFromSlotSuffix(
                            m[1]!,
                            rows
                        );
                    if (slotIndex >= 0) {
                        return {
                            elementId: elId,
                            branch: { type: "slot", slotIndex },
                        };
                    }
                }
            }
        }
    }
    const schedDay = resolveSchedulerDayFromChainNode(
        fromNodeId,
        nodesById,
        edges
    );
    if (schedDay) {
        return {
            elementId: schedDay.elementId,
            branch: { type: "day", dayOfWeek: schedDay.dayOfWeek },
        };
    }
    return null;
}

function resolvePlaybackBranchLocksFromNode(
    fromNodeId: string,
    nodesById: Map<string, IvrPlaybackGraphNode>,
    routes: IvrApiRoute[],
    edges: IvrPlaybackGraphEdge[]
): PlaybackBranchLocks {
    const dtmfKeyByElement = new Map<number, string>();
    const routeOverrideByElement = new Map<number, IvrApiRoute>();
    const schedulerBranchByElement = new Map<
        number,
        SchedulerPlaybackBranch
    >();
    let retriesOutcome: "true" | "false" = "true";

    const menuElId = menuElementIdFromChainNode(fromNodeId, nodesById);

    const applyKey = (key: string, exceeded: boolean) => {
        if (menuElId == null) return;
        dtmfKeyByElement.set(menuElId, key);
        const hit = findMenuRouteForKey(menuElId, routes, key, exceeded);
        if (hit) routeOverrideByElement.set(menuElId, hit);
    };

    if (fromNodeId.endsWith("-false")) {
        retriesOutcome = "false";
        const keyMatch = fromNodeId.match(/-retries-([0-9*#]+)-false$/);
        if (keyMatch) {
            applyKey(keyMatch[1], true);
        }
    } else if (
        fromNodeId.endsWith("-true") &&
        fromNodeId.includes("-retries-")
    ) {
        retriesOutcome = "true";
        const keyMatch = fromNodeId.match(/-retries-([0-9*#]+)-true$/);
        if (keyMatch) {
            applyKey(keyMatch[1], false);
        }
    } else {
        const keyFromRetries = fromNodeId.match(/-retries-([0-9*#]+)$/);
        if (keyFromRetries) {
            retriesOutcome = "true";
            applyKey(keyFromRetries[1], false);
        } else {
            const keyFromDtmf = fromNodeId.match(/-dtmf-([0-9*#]+)$/);
            if (keyFromDtmf) {
                applyKey(keyFromDtmf[1], false);
            }
        }
    }

    const schedBranch = resolveSchedulerBranchFromChainNode(
        fromNodeId,
        nodesById,
        edges
    );
    if (schedBranch) {
        schedulerBranchByElement.set(
            schedBranch.elementId,
            schedBranch.branch
        );
    }

    return {
        dtmfKeyByElement,
        routeOverrideByElement,
        schedulerBranchByElement,
        retriesOutcome,
    };
}

function lockedMenuDtmfKeyForElement(
    elementId: number | null,
    ctx: WalkContext
): string | undefined {
    if (elementId == null) return ctx.menuDtmfKey;
    return (
        ctx.lockedDtmfKeyByElement?.get(elementId) ?? ctx.menuDtmfKey
    );
}

function pickRetriesChildId(
    retriesNodeId: string,
    outs: string[],
    outcome: "true" | "false"
): string | null {
    const child = `${retriesNodeId}-${outcome}`;
    return outs.includes(child) ? child : null;
}

function chooseNextNode(currentId: string, ctx: WalkContext): string | null {
    const outs = ctx.outIndex.get(currentId) ?? [];
    if (!outs.length) return null;

    if (ctx.stopAtDtmfHub && isMenuDtmfHubNode(currentId)) {
        return null;
    }

    const retriesOutcome = ctx.retriesOutcome ?? "true";

    const retriesHub = currentId.match(/-retries-([0-9*#]+)$/);
    if (retriesHub) {
        const hit = pickRetriesChildId(currentId, outs, retriesOutcome);
        if (hit) return hit;
    }
    if (currentId.endsWith("-timeout-retries")) {
        const hit = pickRetriesChildId(currentId, outs, retriesOutcome);
        if (hit) return hit;
    }
    if (currentId.endsWith("-others-retries")) {
        const hit = pickRetriesChildId(currentId, outs, retriesOutcome);
        if (hit) return hit;
    }

    if (isSchedulerConfigNode(currentId, ctx)) {
        const elId = resolveElementId(
            ctx.nodesById.get(currentId)?.data?.blockConfig?.elementId
        );
        const lockedBranch =
            elId != null
                ? ctx.lockedSchedulerBranchByElement?.get(elId)
                : undefined;
        const chainPrefix = `${CHAIN_PREFIX}${currentId}-`;
        const dayNodes = outs.filter(
            (id) =>
                id.startsWith(chainPrefix) && /-day-\d+$/.test(id)
        );
        const slotNodes = outs.filter(
            (id) =>
                id.startsWith(chainPrefix) && /-sched-slot-/.test(id)
        );
        const othersId = schedulerOthersNodeId(currentId);
        if (lockedBranch?.type === "day") {
            const dayId = schedulerDayNodeId(
                currentId,
                lockedBranch.dayOfWeek
            );
            if (outs.includes(dayId)) return dayId;
        }
        if (lockedBranch?.type === "chain") {
            if (outs.includes(lockedBranch.nodeId)) {
                return lockedBranch.nodeId;
            }
        }
        if (lockedBranch?.type === "slot") {
            const sortedRows = sortedSchedulerSlotRowsFromConfigNode(
                currentId,
                ctx
            );
            const slotId = schedulerSlotNodeIdForIndex(
                currentId,
                sortedRows,
                lockedBranch.slotIndex
            );
            if (outs.includes(slotId)) return slotId;
        }
        if (lockedBranch?.type === "others" && outs.includes(othersId)) {
            return othersId;
        }
        const chainDefault = schedulerDefaultChainChildId(
            currentId,
            outs,
            ctx
        );
        if (chainDefault) return chainDefault;
        if (!dayNodes.length && slotNodes.length > 0) {
            return (
                orderSchedulerSlotNodeIds(
                    currentId,
                    slotNodes,
                    ctx
                )[0] ?? null
            );
        }
        const chainOrdered = buildSchedulerChainBranches(
            currentId,
            ctx.nodesById,
            ctx.outIndex
        );
        for (const branch of chainOrdered) {
            if (outs.includes(branch.nodeId)) return branch.nodeId;
        }
        return (
            sortNodesByLayout(
                dayNodes.length ? dayNodes : outs,
                ctx.nodesById
            )[0] ?? null
        );
    }

    if (isMenuDtmfHubNode(currentId)) {
        const configNodeId = ctx.nodesById.get(currentId)?.data
            ?.configParentId;
        if (configNodeId) {
            const elId = resolveElementId(
                ctx.nodesById.get(configNodeId)?.data?.blockConfig
                    ?.elementId
            );
            const keys = sortedMenuKeysForConfig(
                configNodeId,
                ctx.nodesById,
                ctx.routes,
                elId
            );
            const key =
                lockedMenuDtmfKeyForElement(elId, ctx) ?? keys[0];
            if (key) {
                const child = menuDtmfKeyNodeId(configNodeId, key);
                if (outs.includes(child)) return child;
            }
        }
    }

    const configTargets = outs.filter((id) =>
        id.startsWith(START_CONFIG_PREFIX)
    );
    if (configTargets.length > 0) {
        const curNode = ctx.nodesById.get(currentId);
        let fromElementId: number | null = null;

        if (curNode?.id.startsWith(START_CONFIG_PREFIX)) {
            fromElementId = resolveElementId(
                curNode.data?.blockConfig?.elementId
            );
        } else if (curNode?.data?.configParentId) {
            const parent = ctx.nodesById.get(curNode.data.configParentId);
            fromElementId = resolveElementId(
                parent?.data?.blockConfig?.elementId
            );
        }

        if (fromElementId != null) {
            const lockedKey = lockedMenuDtmfKeyForElement(
                fromElementId,
                ctx
            );
            const targetEl = resolveRouteTargetElementId(
                fromElementId,
                ctx.routes,
                lockedKey
            );
            if (targetEl != null) {
                const cfg = ctx.elementToConfig.get(targetEl);
                if (cfg && configTargets.includes(cfg)) return cfg;
            }
            if (!lockedKey) {
                const fallbackEl = resolveRouteTargetElementId(
                    fromElementId,
                    ctx.routes
                );
                if (fallbackEl != null) {
                    const cfg = ctx.elementToConfig.get(fallbackEl);
                    if (cfg && configTargets.includes(cfg)) return cfg;
                }
            }
        }

        return sortNodesByLayout(configTargets, ctx.nodesById)[0] ?? null;
    }

    const lockedKey =
        menuElementIdFromChainNode(currentId, ctx.nodesById) != null
            ? lockedMenuDtmfKeyForElement(
                  menuElementIdFromChainNode(currentId, ctx.nodesById),
                  ctx
              )
            : undefined;

    const filtered = outs.filter((id) => {
        if (id.endsWith("-false") && retriesOutcome === "true") {
            return false;
        }
        if (id.endsWith("-true") && retriesOutcome === "false") {
            return false;
        }
        if (lockedKey && /-dtmf-[0-9*#]+$/.test(id)) {
            return id.endsWith(`-dtmf-${lockedKey}`);
        }
        return true;
    });
    const sorted = sortNodesByLayout(
        filtered.length ? filtered : outs,
        ctx.nodesById
    );
    return sorted[0] ?? null;
}

function walkGraphPath(
    startId: string,
    ctx: WalkContext,
    maxSteps = MAX_PLAYBACK_STEPS
): string[] {
    const path: string[] = [];
    let cur: string | null = startId;

    for (let i = 0; i < maxSteps && cur; i++) {
        path.push(cur);
        const next = chooseNextNode(cur, ctx);
        if (!next || next === cur) break;
        cur = next;
    }

    return path;
}

function walkToMenuDtmfHub(
    startId: string,
    menuConfigNodeId: string,
    ctx: WalkContext
): string[] {
    const hubId = menuDtmfHubId(menuConfigNodeId);
    if (!ctx.nodesById.has(hubId)) return [];

    const queue: string[] = [startId];
    const parent = new Map<string, string | null>();
    parent.set(startId, null);

    while (queue.length) {
        const cur = queue.shift()!;
        if (cur === hubId) break;
        for (const next of ctx.outIndex.get(cur) ?? []) {
            if (parent.has(next)) continue;
            parent.set(next, cur);
            queue.push(next);
        }
    }

    if (!parent.has(hubId)) {
        const prefix = walkGraphPath(startId, { ...ctx, stopAtDtmfHub: true });
        if (!prefix.includes(hubId)) return [];
        return prefix.slice(0, prefix.indexOf(hubId) + 1);
    }

    const path: string[] = [];
    let node: string | null = hubId;
    while (node) {
        path.unshift(node);
        node = parent.get(node) ?? null;
    }
    return path;
}

function mergeNodePaths(...segments: string[][]): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const seg of segments) {
        for (const id of seg) {
            if (seen.has(id)) continue;
            seen.add(id);
            out.push(id);
        }
    }
    return out;
}

function resolveEntryStartNodeId(
    nodes: IvrPlaybackGraphNode[],
    edges: IvrPlaybackGraphEdge[],
    entryElementId: number | null | undefined,
    elementToConfig: Map<number, string>
): string | null {
    const fromBase = edges.find((e) => e.source === BASE_NODE_ID);
    if (fromBase && nodes.some((n) => n.id === fromBase.target)) {
        return fromBase.target;
    }

    const entry = entryElementId ?? 0;
    if (entry > 0) {
        const cfg = elementToConfig.get(entry);
        if (cfg) return cfg;
    }

    const firstConfig = nodes.find((n) =>
        n.id.startsWith(START_CONFIG_PREFIX)
    );
    return firstConfig?.id ?? null;
}

type MenuBranchPoint = {
    elementId: number;
    configNodeId: string | null;
    keys: string[];
};

type SchedulerBranchPoint = {
    elementId: number;
    configNodeId: string;
    days: { dayOfWeek: number; dayNodeId: string; label: string }[];
    slots: { slotIndex: number; slotNodeId: string; label: string }[];
    chainBranches: { nodeId: string; label: string }[];
    hasOthers: boolean;
    othersNodeId: string | null;
};

function schedulerChainBranchLabel(
    nodeId: string,
    nodesById: Map<string, IvrPlaybackGraphNode>
): string {
    const label = String(nodesById.get(nodeId)?.data?.label ?? "")
        .replace(/\n/g, " ")
        .trim();
    if (label) return label;
    if (nodeId.endsWith("-daily-primary")) return "Primary";
    if (nodeId.endsWith("-daily-rest")) return "Rest";
    if (nodeId.endsWith("-sched-others")) return "Others";
    return nodeId;
}

function buildSchedulerChainBranches(
    configNodeId: string,
    nodesById: Map<string, IvrPlaybackGraphNode>,
    outIndex: Map<string, string[]>
): SchedulerBranchPoint["chainBranches"] {
    const chainPrefix = `${CHAIN_PREFIX}${configNodeId}-`;
    const configNode = nodesById.get(configNodeId);
    const branches: SchedulerBranchPoint["chainBranches"] = [];

    const primaryId = schedulerDailyPrimaryNodeId(configNodeId);
    const restId = schedulerDailyRestNodeId(configNodeId);
    const othersId = schedulerOthersNodeId(configNodeId);

    if (nodesById.has(primaryId)) {
        branches.push({
            nodeId: primaryId,
            label: schedulerChainBranchLabel(primaryId, nodesById),
        });
    }
    if (nodesById.has(restId)) {
        branches.push({
            nodeId: restId,
            label: schedulerChainBranchLabel(restId, nodesById),
        });
    }

    if (!nodesById.has(primaryId)) {
        const sortedRows = [
            ...((configNode?.data?.blockConfig?.schedulerSlotRows as
                | IvrSchedulerSlotRow[]
                | undefined) ?? []),
        ].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
        for (let slotIndex = 0; slotIndex < sortedRows.length; slotIndex++) {
            const row = sortedRows[slotIndex]!;
            const nodeId = schedulerSlotNodeIdForIndex(
                configNodeId,
                sortedRows,
                slotIndex
            );
            if (!nodesById.has(nodeId)) continue;
            branches.push({
                nodeId,
                label: schedulerSlotLabel(
                    row,
                    nodesById.get(nodeId)?.data?.label
                ),
            });
        }
    }

    if (nodesById.has(othersId)) {
        branches.push({
            nodeId: othersId,
            label: schedulerChainBranchLabel(othersId, nodesById),
        });
    }

    return branches;
}

function schedulerDefaultChainChildId(
    configNodeId: string,
    outs: string[],
    ctx: WalkContext
): string | null {
    const branches = buildSchedulerChainBranches(
        configNodeId,
        ctx.nodesById,
        ctx.outIndex
    );
    for (const branch of branches) {
        if (outs.includes(branch.nodeId)) return branch.nodeId;
    }
    return null;
}

function listSchedulerBranchPoints(
    nodes: IvrPlaybackGraphNode[],
    elementToConfig: Map<number, string>,
    outIndex: Map<string, string[]>
): SchedulerBranchPoint[] {
    const nodesById = new Map(nodes.map((n) => [n.id, n]));
    const out: SchedulerBranchPoint[] = [];

    for (const n of nodes) {
        if (!n.id.startsWith(START_CONFIG_PREFIX)) continue;
        if (
            String(n.data?.blockConfig?.startConfiguration ?? "") !==
            "Scheduler"
        ) {
            continue;
        }
        const elId = resolveElementId(n.data?.blockConfig?.elementId);
        if (elId == null) continue;

        const chainPrefix = `${CHAIN_PREFIX}${n.id}-`;
        const dayNodes = nodes.filter(
            (node) =>
                node.id.startsWith(chainPrefix) &&
                /-day-\d+$/.test(node.id)
        );
        const othersNodeId = schedulerOthersNodeId(n.id);
        const hasOthers = nodesById.has(othersNodeId);
        const chainBranches = buildSchedulerChainBranches(
            n.id,
            nodesById,
            outIndex
        );

        const sortedRows = [
            ...((n.data?.blockConfig?.schedulerSlotRows as
                | IvrSchedulerSlotRow[]
                | undefined) ?? []),
        ].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
        const slots: SchedulerBranchPoint["slots"] = [];
        const hasDailyChain =
            nodesById.has(schedulerDailyPrimaryNodeId(n.id)) ||
            nodesById.has(schedulerDailyRestNodeId(n.id));
        if (!dayNodes.length && !hasDailyChain) {
            sortedRows.forEach((row, slotIndex) => {
                const slotNodeId = schedulerSlotNodeIdForIndex(
                    n.id,
                    sortedRows,
                    slotIndex
                );
                if (!nodesById.has(slotNodeId)) return;
                slots.push({
                    slotIndex,
                    slotNodeId,
                    label: schedulerSlotLabel(
                        row,
                        nodesById.get(slotNodeId)?.data?.label
                    ),
                });
            });
        }

        if (
            !dayNodes.length &&
            !hasOthers &&
            !slots.length &&
            !chainBranches.length
        ) {
            continue;
        }

        const days = sortNodesByLayout(
            dayNodes.map((dn) => dn.id),
            nodesById
        ).map((dayNodeId) => {
            const dn = nodesById.get(dayNodeId)!;
            const m = dayNodeId.match(/-day-(\d+)$/);
            const dayOfWeek = m ? parseInt(m[1]!, 10) : 0;
            const label = String(dn.data?.label ?? dayOfWeek)
                .split("\n")[0]!
                .trim();
            return { dayOfWeek, dayNodeId, label };
        });

        out.push({
            elementId: elId,
            configNodeId: n.id,
            days,
            slots,
            chainBranches,
            hasOthers,
            othersNodeId: hasOthers ? othersNodeId : null,
        });
    }

    return out;
}

function schedulerBranchPlaybackOptions(
    sched: SchedulerBranchPoint
): SchedulerPlaybackBranch[] {
    if (sched.days.length > 0) {
        const options: SchedulerPlaybackBranch[] = sched.days.map((day) => ({
            type: "day",
            dayOfWeek: day.dayOfWeek,
        }));
        if (sched.hasOthers) {
            options.push({ type: "others" });
        }
        return options;
    }

    if (sched.slots.length > 0) {
        const options: SchedulerPlaybackBranch[] = sched.slots.map(
            (slot) => ({ type: "slot", slotIndex: slot.slotIndex })
        );
        if (sched.hasOthers) {
            options.push({ type: "others" });
        }
        return options;
    }

    if (sched.chainBranches.length > 0) {
        return sched.chainBranches.map((branch) => {
            if (branch.nodeId.endsWith("-sched-others")) {
                return { type: "others" as const };
            }
            return { type: "chain" as const, nodeId: branch.nodeId };
        });
    }

    if (sched.hasOthers) {
        return [{ type: "others" }];
    }
    return [];
}

function buildSchedulerBranchLockCombos(
    elementIds: number[],
    schedBranches: SchedulerBranchPoint[]
): Map<number, SchedulerPlaybackBranch>[] {
    const onPath = schedBranches.filter((sb) =>
        elementIds.includes(sb.elementId)
    );
    const withOptions = onPath.filter(
        (sb) =>
            sb.days.length > 0 ||
            sb.slots.length > 0 ||
            sb.chainBranches.length > 0 ||
            sb.hasOthers
    );
    if (!withOptions.length) return [new Map()];

    let combos: Map<number, SchedulerPlaybackBranch>[] = [new Map()];
    for (const sched of withOptions) {
        const options = schedulerBranchPlaybackOptions(sched);
        if (!options.length) continue;
        const next: Map<number, SchedulerPlaybackBranch>[] = [];
        for (const combo of combos) {
            for (const opt of options) {
                const m = new Map(combo);
                m.set(sched.elementId, opt);
                next.push(m);
            }
        }
        combos = next;
    }
    return combos;
}

function schedulerBranchPlanKeySuffix(
    branchLock: Map<number, SchedulerPlaybackBranch>
): string {
    return Array.from(branchLock.entries())
        .map(([el, branch]) => {
            if (branch.type === "others") return `${el}-others`;
            if (branch.type === "slot") return `${el}-slot-${branch.slotIndex}`;
            if (branch.type === "chain") {
                const tail = branch.nodeId.split("-").slice(-2).join("-");
                return `${el}-chain-${tail}`;
            }
            return `${el}-day-${branch.dayOfWeek}`;
        })
        .join("-");
}

function buildMenuBranchGraphPath(
    startId: string,
    menu: MenuBranchPoint,
    key: string,
    baseCtx: WalkContext,
    schedulerBranchByElement?: ReadonlyMap<number, SchedulerPlaybackBranch>
): string[] {
    const pick = new Map<number, string>([[menu.elementId, key]]);
    const walkCtx: WalkContext = {
        ...baseCtx,
        menuDtmfKey: key,
        lockedDtmfKeyByElement: pick,
        lockedSchedulerBranchByElement: schedulerBranchByElement,
    };

    if (!menu.configNodeId) {
        return walkGraphPath(startId, walkCtx);
    }

    const hubId = menuDtmfHubId(menu.configNodeId);
    const keyNodeId = menuDtmfKeyNodeId(menu.configNodeId, key);
    if (
        !baseCtx.nodesById.has(hubId) ||
        !baseCtx.nodesById.has(keyNodeId)
    ) {
        return walkGraphPath(startId, walkCtx);
    }

    const hubPrefix = walkToMenuDtmfHub(
        startId,
        menu.configNodeId,
        baseCtx
    );
    const branch = walkGraphPath(keyNodeId, walkCtx);
    return mergeNodePaths(hubPrefix, branch);
}

function schedulerBranchLockLabels(
    schedOnPath: SchedulerBranchPoint[],
    branchLock: Map<number, SchedulerPlaybackBranch>
): string[] {
    return schedOnPath
        .map((s) => {
            const lock = branchLock.get(s.elementId);
            if (!lock) return null;
            if (lock.type === "others") return "Others";
            if (lock.type === "chain") {
                const hit = s.chainBranches.find(
                    (b) => b.nodeId === lock.nodeId
                );
                return hit?.label ?? null;
            }
            if (lock.type === "slot") {
                const slot = s.slots.find(
                    (sl) => sl.slotIndex === lock.slotIndex
                );
                return slot?.label ?? null;
            }
            const day = s.days.find((d) => d.dayOfWeek === lock.dayOfWeek);
            return day?.label ?? null;
        })
        .filter((l): l is string => Boolean(l));
}

function schedulerBranchPlanLabel(
    key: string,
    schedOnPath: SchedulerBranchPoint[],
    branchLock: Map<number, SchedulerPlaybackBranch>
): string {
    const branchLabels = schedulerBranchLockLabels(schedOnPath, branchLock);
    return branchLabels.length
        ? `Кнопка «${key}» · ${branchLabels.join(" · ")}`
        : `Кнопка «${key}»`;
}

function listMenuBranchPoints(
    nodes: IvrPlaybackGraphNode[],
    elements: IvrApiElement[],
    routes: IvrApiRoute[],
    elementToConfig: Map<number, string>
): MenuBranchPoint[] {
    const nodesById = new Map(nodes.map((n) => [n.id, n]));
    const byElement = new Map<number, MenuBranchPoint>();

    const upsert = (
        elementId: number,
        configNodeId: string | null,
        extraKeys: string[]
    ) => {
        const fromRoutes = deriveMenuDtmfKeysFromRoutes(elementId, routes);
        const fromConfig =
            configNodeId != null
                ? sortedMenuKeysForConfig(
                      configNodeId,
                      nodesById,
                      routes,
                      elementId
                  )
                : [];
        const keys = sortIvrMenuDtmfKeys([
            ...fromRoutes,
            ...fromConfig,
            ...extraKeys,
        ]);
        if (keys.length === 0) return;

        const prev = byElement.get(elementId);
        if (!prev) {
            byElement.set(elementId, {
                elementId,
                configNodeId,
                keys,
            });
            return;
        }
        const merged = sortIvrMenuDtmfKeys([...prev.keys, ...keys]);
        byElement.set(elementId, {
            elementId,
            configNodeId: prev.configNodeId ?? configNodeId,
            keys: merged,
        });
    };

    for (const el of elements) {
        const elId = Number(el.elementId);
        if (!Number.isFinite(elId)) continue;
        const isMenu =
            String(el.elementType ?? "").toUpperCase() === "MENU";
        const keys = deriveMenuDtmfKeysFromRoutes(elId, routes);
        if (!isMenu && keys.length === 0) continue;
        upsert(elId, elementToConfig.get(elId) ?? null, []);
    }

    for (const n of nodes) {
        if (!n.id.startsWith(START_CONFIG_PREFIX)) continue;
        const elId = resolveElementId(n.data?.blockConfig?.elementId);
        if (elId == null) continue;
        const isMenu =
            String(n.data?.blockConfig?.startConfiguration ?? "") ===
                "Menu" ||
            String(
                elements.find((e) => Number(e.elementId) === elId)
                    ?.elementType ?? ""
            ).toUpperCase() === "MENU";
        if (!isMenu) continue;
        upsert(elId, n.id, []);
    }

    return Array.from(byElement.values()).filter((b) => b.keys.length > 0);
}

function planSignature(elementIds: number[], nodeIds: string[]): string {
    return `${elementIds.join(",")}|${nodeIds.join("|")}`;
}

export function resolvePlaybackElementId(
    nodeId: string,
    nodesById: Map<string, IvrPlaybackGraphNode>,
    entryElementId: number,
    elementToConfig: Map<number, string>
): number {
    if (nodeId === BASE_NODE_ID) {
        return entryElementId;
    }
    const node = nodesById.get(nodeId);
    if (!node) {
        return entryElementId;
    }
    if (node.id.startsWith(START_CONFIG_PREFIX)) {
        return (
            resolveElementId(node.data?.blockConfig?.elementId) ??
            entryElementId
        );
    }
    const parentId = node.data?.configParentId;
    if (parentId) {
        const parent = nodesById.get(parentId);
        if (parent?.id.startsWith(START_CONFIG_PREFIX)) {
            const el = resolveElementId(parent.data?.blockConfig?.elementId);
            if (el != null) return el;
        }
    }
    return entryElementId;
}

function buildSingleBranchPlaybackPlan(params: {
    startId: string;
    pathEntryElementId: number;
    nodesById: Map<string, IvrPlaybackGraphNode>;
    outIndex: Map<string, string[]>;
    edges: IvrPlaybackGraphEdge[];
    elementToConfig: Map<number, string>;
    elementsById: Map<number, IvrApiElement>;
    routes: IvrApiRoute[];
    schedules?: IvrApiSchedule[];
    fromNodeId: string;
}): IvrPlaybackPlan | null {
    const {
        startId,
        pathEntryElementId,
        nodesById,
        outIndex,
        edges,
        elementToConfig,
        elementsById,
        routes,
        schedules,
        fromNodeId,
    } = params;

    const locks = resolvePlaybackBranchLocksFromNode(
        fromNodeId,
        nodesById,
        routes,
        edges
    );

    const lockedCtx: WalkContext = {
        nodesById,
        outIndex,
        routes,
        schedules,
        elementsById,
        elementToConfig,
        lockedDtmfKeyByElement: locks.dtmfKeyByElement,
        lockedSchedulerBranchByElement: locks.schedulerBranchByElement,
        retriesOutcome: locks.retriesOutcome,
        menuDtmfKey: locks.dtmfKeyByElement.values().next().value,
    };

    const elementIds = buildElementIdPath(
        pathEntryElementId,
        elementsById,
        routes,
        schedules,
        locks.dtmfKeyByElement,
        locks.routeOverrideByElement
    );

    let nodeIds = elementPathToNodeIds(
        elementIds,
        startId,
        elementToConfig,
        outIndex,
        nodesById,
        locks.dtmfKeyByElement,
        lockedCtx
    );

    const graphPath = walkGraphPath(startId, lockedCtx);
    if (graphPath.length > nodeIds.length) {
        nodeIds = graphPath;
    } else if (nodeIds.length <= 1 && graphPath.length) {
        nodeIds = graphPath;
    } else if (nodeIds.length) {
        const lastId = nodeIds[nodeIds.length - 1];
        const tail = walkGraphPath(lastId, lockedCtx);
        if (tail.length > 1) {
            nodeIds = mergeNodePaths(nodeIds, tail.slice(1));
        }
    }

    if (!nodeIds.length) return null;

    return {
        key: "from-node",
        label: "Маршрут від елемента",
        nodeIds,
    };
}

export function buildIvrGraphPlaybackPlans(params: {
    nodes: IvrPlaybackGraphNode[];
    edges: IvrPlaybackGraphEdge[];
    entryElementId: number | null | undefined;
    elements: IvrApiElement[];
    routes: IvrApiRoute[];
    schedules?: IvrApiSchedule[];
    fromNodeId?: string | null;
}): IvrPlaybackPlan[] {
    const { nodes, edges, routes, schedules } = params;
    if (!nodes.length) return [];

    const nodesById = new Map(nodes.map((n) => [n.id, n]));
    const outIndex = buildOutgoingIndex(edges);
    const elementToConfig = buildElementIdToConfigNodeId(nodes);
    const elementsById = new Map(
        (params.elements ?? []).map((e) => [Number(e.elementId), e])
    );

    const entry = resolveEffectiveIvrEntryElementId(
        params.entryElementId,
        params.elements ?? []
    );

    let startId = resolveEntryStartNodeId(
        nodes,
        edges,
        entry,
        elementToConfig
    );
    if (!startId) return [];

    let pathEntryElementId = entry;
    const fromNodeId = params.fromNodeId?.trim() || null;
    if (fromNodeId && nodesById.has(fromNodeId)) {
        startId = fromNodeId;
        pathEntryElementId = resolvePlaybackElementId(
            fromNodeId,
            nodesById,
            entry,
            elementToConfig
        );

        const singlePlan = buildSingleBranchPlaybackPlan({
            startId,
            pathEntryElementId,
            nodesById,
            outIndex,
            edges,
            elementToConfig,
            elementsById,
            routes,
            schedules,
            fromNodeId,
        });
        return singlePlan ? [singlePlan] : [];
    }

    const baseCtx: WalkContext = {
        nodesById,
        outIndex,
        routes,
        schedules,
        elementsById,
        elementToConfig,
    };

    const menuBranches = listMenuBranchPoints(
        nodes,
        params.elements ?? [],
        routes,
        elementToConfig
    );
    const schedBranches = listSchedulerBranchPoints(
        nodes,
        elementToConfig,
        outIndex
    );

    const emptyPick = new Map<number, string>();
    const mainElementIds = buildElementIdPath(
        pathEntryElementId,
        elementsById,
        routes,
        schedules,
        emptyPick
    );

    let mainNodeIds = elementPathToNodeIds(
        mainElementIds,
        startId,
        elementToConfig,
        outIndex,
        nodesById,
        emptyPick,
        baseCtx
    );
    if (mainNodeIds.length <= 1) {
        mainNodeIds = walkGraphPath(startId, baseCtx);
    }

    const plans: IvrPlaybackPlan[] = [];
    const seen = new Set<string>();

    const pushPlan = (
        key: string,
        label: string,
        elementIds: number[],
        nodeIds: string[]
    ) => {
        if (!nodeIds.length) return;
        const sig = planSignature(elementIds, nodeIds);
        if (seen.has(sig)) return;
        seen.add(sig);
        plans.push({ key, label, nodeIds });
    };

    const entryMenu = menuBranches.find(
        (m) => m.elementId === pathEntryElementId
    );
    const entryRoutedKeys = entryMenu
        ? menuDtmfKeysWithRoutes(
              entryMenu.elementId,
              entryMenu.keys,
              routes
          )
        : [];
    const skipMainPlan = entryRoutedKeys.length > 1;

    const schedOnMainPath = schedBranches.filter((sb) =>
        mainElementIds.includes(sb.elementId)
    );
    const mainSchedulerCombos =
        schedOnMainPath.length > 0
            ? buildSchedulerBranchLockCombos(
                  mainElementIds,
                  schedBranches
              )
            : [];

    if (!skipMainPlan) {
        if (mainSchedulerCombos.length > 1) {
            for (const branchLock of mainSchedulerCombos) {
                const walkCtx: WalkContext = {
                    ...baseCtx,
                    lockedSchedulerBranchByElement: branchLock,
                };
                let nodeIds = walkGraphPath(startId, walkCtx);
                if (nodeIds.length <= 1) {
                    nodeIds = elementPathToNodeIds(
                        mainElementIds,
                        startId,
                        elementToConfig,
                        outIndex,
                        nodesById,
                        emptyPick,
                        walkCtx
                    );
                }
                const branchLabels = schedulerBranchLockLabels(
                    schedOnMainPath,
                    branchLock
                );
                const label = branchLabels.length
                    ? `Розклад · ${branchLabels.join(" · ")}`
                    : "Основний маршрут";
                pushPlan(
                    `main-${schedulerBranchPlanKeySuffix(branchLock)}`,
                    label,
                    mainElementIds,
                    nodeIds
                );
            }
        } else {
            pushPlan(
                "main",
                "Основний маршрут",
                mainElementIds,
                mainNodeIds
            );
        }
    }

    const menuElementIds = new Set(
        menuBranches.map((m) => m.elementId)
    );

    for (const menu of menuBranches) {
        const routedKeys = menuDtmfKeysWithRoutes(
            menu.elementId,
            menu.keys,
            routes
        );
        for (const key of routedKeys) {
            const pick = new Map<number, string>([
                [menu.elementId, key],
            ]);
            const elementIds = buildElementIdPath(
                pathEntryElementId,
                elementsById,
                routes,
                schedules,
                pick
            );

            const schedOnPath = schedBranches.filter((sb) =>
                elementIds.includes(sb.elementId)
            );
            const branchCombos = buildSchedulerBranchLockCombos(
                elementIds,
                schedBranches
            );

            for (const branchLock of branchCombos) {
                const nodeIds = buildMenuBranchGraphPath(
                    startId,
                    menu,
                    key,
                    baseCtx,
                    branchLock.size ? branchLock : undefined
                );
                const planKey =
                    branchLock.size > 0
                        ? `${menu.elementId}-dtmf-${key}-${schedulerBranchPlanKeySuffix(branchLock)}`
                        : `${menu.elementId}-dtmf-${key}`;
                const label =
                    schedOnPath.length > 0 && branchLock.size > 0
                        ? schedulerBranchPlanLabel(
                              key,
                              schedOnPath,
                              branchLock
                          )
                        : `Кнопка «${key}»`;

                pushPlan(planKey, label, elementIds, nodeIds);
            }
        }
    }

    for (const el of params.elements ?? []) {
        const elId = Number(el.elementId);
        if (!Number.isFinite(elId)) continue;
        const branches = uniqueOutgoingRoutes(elId, routes);
        if (branches.length <= 1) continue;

        for (const route of branches) {
            const rv = (route.routeValue ?? "").trim();
            const nt = normalizeRouteType(route.routeType);
            if (
                menuElementIds.has(elId) &&
                (nt === "DTMF" || nt === "DTMF_EXCEEDED") &&
                rv
            ) {
                continue;
            }

            const pick = new Map<number, string>();
            if (rv && (nt === "DTMF" || nt === "DTMF_EXCEEDED")) {
                pick.set(elId, rv);
            }
            const routeOverride = new Map<number, IvrApiRoute>([
                [elId, route],
            ]);
            const elementIds = buildElementIdPath(
                pathEntryElementId,
                elementsById,
                routes,
                schedules,
                pick,
                routeOverride
            );
            let nodeIds = elementPathToNodeIds(
                elementIds,
                startId,
                elementToConfig,
                outIndex,
                nodesById,
                pick,
                baseCtx
            );
            pushPlan(
                `${elId}-route-${route.toElementId}-${nt}-${rv}`,
                routeBranchLabel(route),
                elementIds,
                nodeIds
            );
        }
    }

    if (plans.length <= 1) {
        const defaultKey = menuBranches[0]?.keys[0];
        const graphMain = walkGraphPath(startId, {
            ...baseCtx,
            menuDtmfKey: defaultKey,
        });
        if (graphMain.length > (plans[0]?.nodeIds.length ?? 0)) {
            plans[0] = {
                key: "main",
                label: "Основний маршрут",
                nodeIds: graphMain,
            };
        }
    }

    return plans;
}

export function buildMostLikelyIvrPlaybackPath(params: {
    entryElementId: number | null | undefined;
    elements: IvrApiElement[];
    routes: IvrApiRoute[];
    schedules?: IvrApiSchedule[];
}): number[] {
    const { elements, routes, schedules } = params;
    if (!elements?.length) return [];

    const elementsById = new Map(
        elements.map((e) => [Number(e.elementId), e])
    );
    const entry = resolveEffectiveIvrEntryElementId(
        params.entryElementId,
        elements
    );
    if (!entry || !elementsById.has(entry)) return [];

    const path: number[] = [];
    const visited = new Set<number>();
    let current: number | null = entry;

    while (
        current != null &&
        current > 0 &&
        !visited.has(current) &&
        path.length < MAX_PLAYBACK_STEPS
    ) {
        visited.add(current);
        path.push(current);

        const el = elementsById.get(current);
        if (!el) break;
        if (String(el.elementType ?? "").toUpperCase() === "END") break;

        const config = elementToBlockConfig(el, routes, schedules);
        const configType = String(config.startConfiguration ?? "Play");

        if (configType === "Scheduler") {
            current = schedulerFirstChildElementId(
                current,
                config,
                schedules,
                elementsById
            );
            continue;
        }

        const outs = routesFromElementSorted(current, routes);
        if (outs.length > 0) {
            current = outs[0].toElementId;
            continue;
        }

        const schedSorted = [...(schedules ?? [])]
            .filter((s) => ivrRawScheduleIsActive(s))
            .filter((s) => ivrRawScheduleElementId(s) === current)
            .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
        let next: number | null = null;
        for (const s of schedSorted) {
            const tid = ivrRawScheduleActiveToId(s);
            if (tid !== 0 && elementsById.has(tid)) {
                next = tid;
                break;
            }
        }
        current = next;
    }

    return path;
}
